import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { prefersReducedMotion } from './motion.js';
import { seedFlightStars } from './orbitScene.js';
import { rocketSvg } from './shipArt.js';
import './ship.css';

/** Cuánto queda a la vista, entre que termina de nacer y empieza a volver a Perfil. */
export const TRAVEL_NOTICE_MS = 4200;

/** El botón de Perfil del que nace el aviso: la pestaña de abajo (celular) o el menú lateral (compu). */
export const PROFILE_NAV_SELECTOR = '[data-nav="profile"]';

/**
 * El reproductor chico de la Audioteca (`GlobalListenBar`), apoyado arriba de las pestañas: suele
 * estar abierto después de la Antena, y el aviso se le paraba encima (card 599). Si está, el aviso
 * va arriba de él.
 */
export const LISTEN_BAR_SELECTOR = '.glb-wrap';

/**
 * El lugar del menú lateral donde entra el aviso en la compu (`DesktopSidebar`, debajo de Perfil):
 * `display: contents`, así sólo ocupa lugar mientras el aviso está.
 */
export const SIDE_SLOT_SELECTOR = '[data-side-slot]';

/** Lo que tarda el menú de la compu en hacerle lugar (y en volver a ocupar el suyo). */
export const NOTICE_ROOM_MS = 280;

/**
 * Dónde va el aviso (card 599: la #596 lo vio tapando el reproductor en el celular y el título
 * «Hablemos ahora» en la compu).
 * - Celular: flota justo arriba de la pestaña de Perfil, o arriba del reproductor chico si está
 *   abierto. Puede tapar unos segundos el borde de una tarjeta del Inicio (se desplaza), nunca el
 *   reproductor, las pestañas ni Perfil: por eso crece desde su propio borde de abajo, en la línea
 *   de Perfil, y no desde el centro del ícono. Emmanuel (22/10) descartó que el contenido le hiciera
 *   lugar en el celular: la franja cortaba la pantalla a mitad de una tarjeta y parecía roto.
 * - Compu y tablet: `room` está en el flujo del menú lateral, debajo de Perfil, y la burbuja va
 *   adentro, de su ancho: usa el hueco que el menú ya tiene o empuja la racha (el menú se desplaza).
 * Devuelve el alto que tiene que tener `room` (0 en el celular). La colita apunta al ícono de Perfil.
 */
function placeNotice(
  el: HTMLElement,
  room: HTMLElement | null,
  anchor: HTMLElement,
  variant: 'mobile' | 'desktop',
): number {
  const a = anchor.getBoundingClientRect();
  const icon = anchor.querySelector('svg')?.getBoundingClientRect();
  const ax = icon && icon.width > 0 ? icon.left + icon.width / 2 : a.left + a.width / 2;
  const ay = a.top + a.height / 2;
  let left: number;
  let top: number;
  let roomHeight = 0;
  /** Desde dónde crece y hacia dónde vuelve (alto, relativo a la burbuja): el centro de Perfil. */
  let originY: number | null = null;
  if (variant === 'desktop' && room) {
    const r = room.getBoundingClientRect();
    el.style.width = `${Math.round(r.width)}px`;
    left = r.left;
    top = r.top;
    roomHeight = el.offsetHeight;
  } else {
    const w = el.offsetWidth;
    const h = el.offsetHeight;
    let floor = a.top;
    const bar = document.querySelector(LISTEN_BAR_SELECTOR)?.getBoundingClientRect();
    if (bar && bar.height > 0 && bar.top < floor) floor = bar.top;
    left = Math.min(Math.max(12, ax - w + 40), window.innerWidth - w - 12);
    top = floor - h - 16;
    // Crece desde su borde de abajo, en la línea de Perfil: desde el centro del ícono pasaba por
    // encima de las pestañas y del reproductor (la colita sigue apuntando hacia Perfil).
    originY = h + 8;
  }
  el.style.left = `${Math.round(left)}px`;
  el.style.top = `${Math.round(top)}px`;
  // Crece y se achica desde el centro del botón: así «sale» y «vuelve» a Perfil.
  el.style.transformOrigin = `${Math.round(ax - left)}px ${Math.round(originY ?? ay - top)}px`;
  // La colita, fuera de las esquinas redondeadas.
  const tail = Math.min(Math.max(ax - left, 24), Math.max(24, el.offsetWidth - 24));
  el.style.setProperty('--tail-x', `${Math.round(tail)}px`);
  return Math.max(0, Math.round(roomHeight));
}

/**
 * «Tu nave está en viaje» (card #585, opción B que eligió Emmanuel el 21/10): una burbuja chica que
 * nace de Perfil con rebote, muestra la nave volando y a los pocos segundos vuelve a meterse ahí.
 * Reemplaza al cartel que tapaba la línea de la rutina durante cinco entradas. Sale una sola vez:
 * `onShown` avisa cuando aparece (el Inicio lo marca como visto) y `onDone` cuando ya volvió.
 * Con movimiento reducido, un fundido para entrar y otro para irse; la nave queda quieta.
 * La burbuja se dibuja en el `body` (portal). En la compu, primero el menú le hace lugar (`room`, ver
 * `placeNotice`), después nace, y al volver a Perfil el menú recupera su lugar; en el celular flota
 * arriba de las pestañas y del reproductor (card 599).
 */
export function ShipTravelNotice(props: {
  variant: 'mobile' | 'desktop';
  onShown: () => void;
  onOpen: () => void;
  onDone: () => void;
}) {
  const { variant } = props;
  const box = useRef<HTMLButtonElement>(null);
  const room = useRef<HTMLDivElement>(null);
  const stars = useRef<HTMLSpanElement>(null);
  const cb = useRef(props);
  cb.current = props;
  const [place, setPlace] = useState<{ anchor: HTMLElement; slot: HTMLElement | null } | null>(
    null,
  );
  /** El alto que el contenido le deja; `open` = ya le hizo lugar (o se lo está haciendo). */
  const want = useRef(0);
  const open = useRef(false);

  // El ancla tiene que estar: el Inicio siempre muestra la barra de pestañas o el menú lateral. En la
  // compu, también el lugar del menú donde entra.
  useLayoutEffect(() => {
    const anchor = document.querySelector<HTMLElement>(PROFILE_NAV_SELECTOR);
    const slot =
      variant === 'desktop' ? document.querySelector<HTMLElement>(SIDE_SLOT_SELECTOR) : null;
    if (!anchor || (variant === 'desktop' && !slot)) {
      console.warn('[ship] aviso «en viaje»: no está el botón de Perfil o su lugar en el menú');
      cb.current.onDone();
      return;
    }
    setPlace({ anchor, slot });
  }, [variant]);

  useLayoutEffect(() => {
    const el = box.current;
    const r = room.current;
    if (!place || !el) return;
    const measure = () => {
      want.current = placeNotice(el, r, place.anchor, variant);
      const h = `${open.current ? want.current : 0}px`;
      if (r && r.style.height !== h) r.style.height = h;
    };
    measure();
    if (stars.current && stars.current.childElementCount === 0) seedFlightStars(stars.current);
    // El reproductor puede estar llegando (sube con una transición cuando aparecen las pestañas) o
    // aparecer mientras el aviso está a la vista: se vuelve a medir en cada cuadro.
    let frame = requestAnimationFrame(function follow() {
      measure();
      frame = requestAnimationFrame(follow);
    });
    return () => cancelAnimationFrame(frame);
  }, [place, variant]);

  useEffect(() => {
    const el = box.current;
    const r = room.current;
    if (!place || !el) return;
    const icon = place.anchor.querySelector('svg');
    const reduced = prefersReducedMotion();
    const running: Animation[] = [];
    const play = (target: Element | null, frames: Keyframe[], opts: KeyframeAnimationOptions) => {
      if (!target) return null;
      const anim = target.animate(frames, opts);
      running.push(anim);
      return anim;
    };
    let stopped = false;
    const timers: ReturnType<typeof setTimeout>[] = [];
    const later = (fn: () => void, ms: number) => timers.push(setTimeout(fn, ms));
    // En la compu el menú le hace lugar (sin movimiento: de una) y recién ahí nace la burbuja; en el
    // celular no hay lugar que hacer (`r` es null) y nace enseguida.
    const roomMs = reduced || !r ? 0 : NOTICE_ROOM_MS;
    r?.classList.toggle('is-still', reduced);
    const setRoom = (on: boolean) => {
      open.current = on;
      if (r) r.style.height = `${on ? want.current : 0}px`;
    };
    setRoom(true);

    const appear = () => {
      const enter = reduced
        ? play(el, [{ opacity: 0 }, { opacity: 1 }], { duration: 250, fill: 'forwards' })
        : play(
            el,
            [
              { transform: 'scale(0)', opacity: 0 },
              { transform: 'scale(1.1)', opacity: 1, offset: 0.5 },
              { transform: 'scale(0.95)', offset: 0.72 },
              { transform: 'scale(1.02)', offset: 0.88 },
              { transform: 'scale(1)', opacity: 1 },
            ],
            { duration: 680, easing: 'cubic-bezier(0.25, 0.8, 0.35, 1)', fill: 'forwards' },
          );
      if (!reduced) {
        play(
          icon,
          [
            { transform: 'scale(1)' },
            { transform: 'scale(0.8)', offset: 0.4 },
            { transform: 'scale(1.12)', offset: 0.75 },
            { transform: 'scale(1)' },
          ],
          { duration: 420, easing: 'ease-out' },
        );
      }
      cb.current.onShown();
      void enter?.finished.then(
        () => {
          if (!stopped) later(leave, TRAVEL_NOTICE_MS);
        },
        () => undefined,
      );
    };

    if (roomMs === 0) appear();
    else
      later(() => {
        if (!stopped) appear();
      }, roomMs);

    const leave = () => {
      if (stopped) return;
      const out = reduced
        ? play(el, [{ opacity: 1 }, { opacity: 0 }], { duration: 250, fill: 'forwards' })
        : play(
            el,
            [
              { transform: 'scale(1)', opacity: 1 },
              { transform: 'scale(1.06)', opacity: 1, offset: 0.25 },
              { transform: 'scale(0)', opacity: 0.4 },
            ],
            { duration: 460, easing: 'cubic-bezier(0.5, 0, 0.75, 0)', fill: 'forwards' },
          );
      void out?.finished.then(
        () => {
          if (stopped) return;
          // Perfil lo recibe: un saltito.
          if (!reduced) {
            play(
              icon,
              [
                { transform: 'scale(1)' },
                { transform: 'scale(1.35)', offset: 0.35 },
                { transform: 'scale(0.9)', offset: 0.65 },
                { transform: 'scale(1)' },
              ],
              { duration: 620, easing: 'ease-out' },
            );
          }
          // Y el menú vuelve a ocupar su lugar.
          setRoom(false);
          if (roomMs === 0) cb.current.onDone();
          else
            later(() => {
              if (!stopped) cb.current.onDone();
            }, roomMs);
        },
        () => undefined,
      );
    };
    return () => {
      stopped = true;
      timers.forEach(clearTimeout);
      running.forEach((anim) => anim.cancel());
    };
  }, [place]);

  if (!place) return null;
  return (
    <>
      {place.slot &&
        createPortal(
          <div ref={room} className="ship-notice-room is-side" aria-hidden="true" />,
          place.slot,
        )}
      {createPortal(
        <button
          ref={box}
          type="button"
          aria-live="polite"
          className={`ship-notice ${variant === 'desktop' ? 'is-below' : 'is-above'}`}
          onClick={() => cb.current.onOpen()}
        >
          <span className="ship-flight ship-notice-win" aria-hidden="true">
            <span className="ship-stars" ref={stars} />
            <span
              className="ship-flight-craft"
              dangerouslySetInnerHTML={{ __html: rocketSvg({ fire: true, label: '' }) }}
            />
          </span>
          <span className="ship-notice-tx">
            <b>Tu nave está en viaje</b>
            <span>Podés verla desde Perfil</span>
          </span>
          <span className="ship-notice-tail" aria-hidden="true" />
        </button>,
        document.body,
      )}
    </>
  );
}
